import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  Headphones,
  Heart,
  Mic,
  PhoneCall,
  PhoneForwarded,
  PhoneIncoming,
  PhoneOutgoing,
  ShieldCheck,
  Sparkles,
  Star,
  Stethoscope,
  Users,
  Zap
} from 'lucide-react'

/* ────────── AnimatedCounter ────────── */
function AnimatedCounter({ from, to, suffix }: { from: number; to: number; suffix?: string }) {
  const [count, setCount] = useState(from)
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, margin: '-100px' })

  useEffect(() => {
    if (!inView) return
    const duration = 1500
    const start = performance.now()
    const step = (now: number) => {
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setCount(Math.round(from + (to - from) * eased))
      if (progress < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }, [inView, from, to])

  return <span ref={ref}>{count}{suffix}</span>
}

/* ────────── Reveal wrapper ────────── */
function Reveal({ children, className, delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {children}
    </motion.div>
  )
}

/* ────────── DATA ────────── */
const stats = [
  { value: 24, suffix: 'h', label: 'Atendimento ininterrupto' },
  { value: 85, suffix: '%', label: 'Redução de faltas' },
  { value: 3, suffix: 's', label: 'Tempo de resposta' },
  { value: 40, suffix: '%', label: 'Mais agendamentos' }
]

const benefits = [
  {
    icon: PhoneIncoming,
    title: 'Atendimento 24h por voz',
    description: 'Seus pacientes ligam e são atendidos na hora. A IA conversa com voz natural, entende o motivo e resolve — agenda, orienta ou transfere.'
  },
  {
    icon: Calendar,
    title: 'Confirmação e follow-up automático',
    description: 'A IA liga para confirmar consultas 24h antes. Depois, faz follow-up pós-atendimento e já agenda o retorno. Faltas caem até 85%.'
  },
  {
    icon: Users,
    title: 'Triagem por voz',
    description: 'A IA coleta sintomas, histórico e urgência durante a ligação. O dentista já chega na consulta sabendo exatamente o que o paciente precisa.'
  },
  {
    icon: Mic,
    title: 'Voz natural em português',
    description: 'Tom de voz profissional e acolhedor, treinado para o contexto odontológico. O paciente sente que está falando com uma pessoa real.'
  },
  {
    icon: PhoneForwarded,
    title: 'Transferência inteligente',
    description: 'Casos complexos ou urgentes são transferidos automaticamente para o dentista ou recepcionista de plantão, com resumo do que foi conversado.'
  },
  {
    icon: PhoneOutgoing,
    title: 'Disparo de ligações em lote',
    description: 'A IA liga automaticamente para toda sua base de leads — aniversariantes, pacientes inativos, retornos e campanhas. Converta ausentes em agendados.'
  },
  {
    icon: ShieldCheck,
    title: 'Segurança e LGPD',
    description: 'Todas as chamadas são criptografadas. Consentimento registrado, dados protegidos e conformidade total com a legislação brasileira.'
  }
]

const features = [
  {
    icon: PhoneCall,
    title: 'Recepção virtual por voz',
    text: 'Atende todas as chamadas, identifica o paciente pelo número, entende o motivo da ligação e resolve na hora.'
  },
  {
    icon: Calendar,
    title: 'Confirmação automática de consultas',
    text: 'A IA liga para cada paciente 24h antes da consulta. Confirma, reagenda ou cancela sozinha. Sua agenda sempre otimizada.'
  },
  {
    icon: Stethoscope,
    title: 'Triagem pré-consulta',
    text: 'Coleta informações sobre dor, sensibilidade e histórico durante a chamada. O dentista recebe um resumo antes do paciente chegar.'
  },
  {
    icon: Headphones,
    title: 'Follow-up pós-procedimento',
    text: 'A IA liga após extrações, implantes e procedimentos para saber como o paciente está. Reforça cuidados e já agenda o retorno.'
  },
  {
    icon: Zap,
    title: 'Integração com seu sistema',
    text: 'Conecta com qualquer software de gestão odontológica via API. Agendamentos e histórico sincronizados automaticamente.'
  },
  {
    icon: PhoneOutgoing,
    title: 'Disparo em lote para leads',
    text: 'A IA liga automaticamente para listas de pacientes — reativação de inativos, campanhas, aniversariantes. Dezenas de ligações simultâneas.'
  },
  {
    icon: Users,
    title: 'Pipeline de pacientes',
    text: 'Cada lead vira um card com histórico completo de chamadas, estágio no funil e próximas ações automatizadas.'
  }
]

const steps = [
  { step: '01', title: 'Conecte seu número', text: 'Ative em minutos usando seu número atual. Sem trocar de telefone, sem instalar nada.' },
  { step: '02', title: 'Treine sua IA', text: 'Configure o perfil da sua clínica, especialidades, agenda e tom de voz. Tudo pronto em 15 minutos.' },
  { step: '03', title: 'Ative e veja acontecer', text: 'A IA começa a atender. Você acompanha tudo pelo dashboard e ajusta quando quiser.' }
]

const testimonials = [
  {
    quote: 'Reduzimos as faltas em 82% no primeiro mês. A IA liga para confirmar consultas melhor que qualquer recepcionista.',
    author: 'Dra. Mariana Costa',
    role: 'Clínica OdontoBrasil — São Paulo',
    stars: 5
  },
  {
    quote: 'Atendemos 3x mais pacientes novos sem contratar ninguém. As chamadas são tão naturais que os pacientes nem percebem que é IA.',
    author: 'Dr. Ricardo Almeida',
    role: 'Instituto do Sorriso — Curitiba',
    stars: 5
  },
  {
    quote: 'A IA faz triagem por voz e eu já chego na consulta sabendo tudo. Economizo 15 minutos por paciente.',
    author: 'Dra. Fernanda Lima',
    role: 'Clínica Oral Care — Rio de Janeiro',
    stars: 5
  }
]

const plans = [
  {
    name: 'Essencial',
    price: 'R$997',
    desc: 'Para clínicas que querem automatizar o básico.',
    features: ['1 número', 'IA de voz para atendimento', 'Confirmação de consultas', 'Dashboard básico', 'Suporte por email']
  },
  {
    name: 'Profissional',
    price: 'R$1.997',
    desc: 'Para clínicas em crescimento com operação estruturada.',
    features: ['Até 3 números', 'Triagem pré-consulta por voz', 'Pipeline de pacientes', 'Integração via API', 'Relatórios avançados', 'Suporte prioritário'],
    popular: true
  },
  {
    name: 'Enterprise',
    price: 'R$3.997',
    desc: 'Para redes e franquias odontológicas.',
    features: ['Números ilimitados', 'IA personalizada por unidade', 'Multi-tenant completo', 'Webhooks e MCP', 'Gerente de conta dedicado', 'SLA garantido'],
    popular: false
  }
]

const faq = [
  { q: 'Preciso trocar meu número de telefone?', a: 'Não. Você mantém seu número atual. A IA se conecta diretamente à sua linha. Sem trocar de número, sem complicação.' },
  { q: 'A IA entende diferentes sotaques?', a: 'Sim. A IA é treinada em português brasileiro e entende variações regionais, gírias e expressões comuns do dia a dia.' },
  { q: 'Como funciona a integração com minha agenda?', a: 'Conectamos com os principais softwares odontológicos. Se o seu não tiver API, você pode gerenciar os agendamentos pelo nosso dashboard.' },
  { q: 'Meus dados de pacientes estão seguros?', a: 'Sim. Toda comunicação é criptografada de ponta a ponta. Seguimos a LGPD rigorosamente. Você controla quais dados a IA pode acessar.' },
  { q: 'Quanto tempo leva para configurar?', a: 'A ativação básica leva 15 minutos. A personalização completa da IA para sua clínica leva em média 2 dias.' },
  { q: 'Posso testar antes de assinar?', a: 'Sim! Coloque seu número no campo acima e nossa IA vai te ligar agora mesmo. Você escuta a qualidade da voz e a naturalidade do atendimento antes de decidir.' }
]

/* ────────── Pulse dot ────────── */
function PulseDot() {
  return (
    <span className="relative flex h-2.5 w-2.5">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
    </span>
  )
}

/* ════════════════════════════════════════════════════════════════════════════ */
export default function Landing() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  })
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%'])
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0])

  return (
    <main className="min-h-screen bg-white text-slate-900 selection:bg-cyan-100 selection:text-cyan-900 overflow-x-hidden">

      {/* ═══════════════════ NAV ═══════════════════ */}
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="fixed top-0 z-50 w-full border-b border-slate-100 bg-white/80 backdrop-blur-xl"
        role="banner"
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2.5 group" aria-label="AiCall.Odonto Início">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25 transition-transform group-hover:scale-105">
              <PhoneCall className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="text-lg font-bold tracking-tight text-slate-900 font-display">
              AiCall<span className="text-cyan-600">.Odonto</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-8 text-sm font-semibold text-slate-600 lg:flex" aria-label="Navegação principal">
            <a href="#beneficios" className="hover:text-cyan-700 transition-colors focus-visible:ring-2 focus-visible:ring-cyan-500 rounded px-1">Benefícios</a>
            <a href="#funcionalidades" className="hover:text-cyan-700 transition-colors focus-visible:ring-2 focus-visible:ring-cyan-500 rounded px-1">Funcionalidades</a>
            <a href="#como-funciona" className="hover:text-cyan-700 transition-colors focus-visible:ring-2 focus-visible:ring-cyan-500 rounded px-1">Como funciona</a>
            <a href="#planos" className="hover:text-cyan-700 transition-colors focus-visible:ring-2 focus-visible:ring-cyan-500 rounded px-1">Planos</a>
            <a href="#faq" className="hover:text-cyan-700 transition-colors focus-visible:ring-2 focus-visible:ring-cyan-500 rounded px-1">Dúvidas</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden text-sm font-semibold text-slate-700 hover:text-cyan-700 transition-colors sm:inline-flex focus-visible:ring-2 focus-visible:ring-cyan-500 rounded px-1">
              Entrar
            </Link>
            <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }}>
              <a href="#experimente" className="inline-flex h-10 items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-5 text-sm font-bold text-white shadow-lg shadow-cyan-600/20 hover:shadow-cyan-600/30 transition-all focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2">
                Receber ligação demo
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* ═══════════════════ HERO ═══════════════════ */}
      <section ref={heroRef} className="relative overflow-hidden pt-16">
        {/* Animated background blobs */}
        <motion.div
          animate={{ scale: [1, 1.08, 1], rotate: [0, 3, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="absolute -top-40 -right-40 -z-10 h-[700px] w-[700px] rounded-full bg-gradient-to-br from-cyan-200/50 via-blue-200/30 to-transparent blur-3xl"
        />
        <motion.div
          animate={{ scale: [1.05, 1, 1.05], rotate: [0, -3, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
          className="absolute -bottom-20 -left-20 -z-10 h-[600px] w-[600px] rounded-full bg-gradient-to-tr from-cyan-100/40 via-teal-100/20 to-transparent blur-3xl"
        />

        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-10 mx-auto max-w-7xl px-4 pt-20 pb-16 sm:px-6 sm:pt-28 sm:pb-24 lg:px-8 lg:pt-36 lg:pb-32">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            {/* Left content */}
            <div className="max-w-2xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50 px-4 py-1.5 text-sm font-semibold text-cyan-700 font-display shadow-sm"
              >
                <Sparkles className="h-4 w-4 text-cyan-500" aria-hidden="true" />
                IA de voz para clínicas odontológicas
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="mt-6 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl lg:leading-[1.08] font-display"
              >
                Sua clínica{' '}
                <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                  atendendo e ligando
                </span>
                {' '}24h sem contratar ninguém
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.35 }}
                className="mt-6 text-lg leading-relaxed text-slate-600 max-w-xl font-serif"
              >
                IA que atende chamadas, dispara ligações em lote para sua base de leads, confirma consultas e faz follow-up.
                Tudo integrado à sua clínica, como se fosse uma equipe de recepção 24h.
              </motion.p>

              {/* Dr. Caio badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.45 }}
                className="mt-6 flex items-center gap-4 rounded-2xl border border-cyan-100 bg-gradient-to-r from-cyan-50/50 to-blue-50/50 p-4 shadow-sm"
              >
                <img
                  src="/dr-caio-implante.png"
                  alt="Dr. Caio Peixoto — Idealizador AiCall.Odonto"
                  className="h-16 w-16 rounded-full border-2 border-white object-cover object-top shadow-md shadow-cyan-500/10"
                />
                <div>
                  <p className="text-sm font-bold text-slate-800 font-display">Idealizado por Dr. Caio Peixoto</p>
                  <p className="text-xs text-slate-500">Especialista em Implantodontia</p>
                  <p className="text-xs text-cyan-600 font-semibold mt-0.5 font-display">+15 anos transformando sorrisos</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.55 }}
                className="mt-8 flex flex-col gap-3 sm:flex-row"
              >
                <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }}>
                  <a
                    href="#experimente"
                    className="inline-flex h-14 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-8 text-base font-bold text-white shadow-xl shadow-cyan-500/25 hover:shadow-cyan-500/35 transition-all font-display focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2"
                  >
                    Quero receber uma ligação
                    <ArrowRight className="h-5 w-5" aria-hidden="true" />
                  </a>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }}>
                  <a
                    href="#como-funciona"
                    className="inline-flex h-14 items-center justify-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-8 text-base font-bold text-slate-700 transition-all hover:border-cyan-300 hover:text-cyan-700 hover:bg-cyan-50/50 focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2"
                  >
                    Ver como funciona
                  </a>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500 font-display font-medium"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-cyan-600" aria-hidden="true" />
                  <span>Sem fidelidade</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-cyan-600" aria-hidden="true" />
                  <span>Ativação em 5 min</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-cyan-600" aria-hidden="true" />
                  <span>Sem período de teste</span>
                </div>
              </motion.div>
            </div>

            {/* Right: Phone call simulation */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative"
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="mx-auto max-w-[340px] rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl shadow-slate-200/40"
              >
                {/* Call header */}
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 shadow-lg shadow-cyan-500/20">
                    <PhoneCall className="h-6 w-6 text-white" aria-hidden="true" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900 font-display">AiCall.Odonto</p>
                    <p className="text-xs text-slate-500">Atendente virtual</p>
                  </div>
                  <PulseDot />
                </div>

                {/* Call timer */}
                <div className="mt-4 text-center">
                  <motion.p
                    animate={{ opacity: [1, 0.5, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="text-xs font-semibold text-emerald-600 uppercase tracking-wider font-display"
                  >
                    Em chamada
                  </motion.p>
                  <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900 font-display">00:42</p>
                </div>

                {/* Transcript preview */}
                <div className="mt-4 space-y-3 bg-slate-50 border border-slate-100 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-blue-500">
                      <Mic className="h-3.5 w-3.5 text-white" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-display">AiCall.Odonto</p>
                      <p className="text-sm font-medium text-slate-800">Clínica Sorriso, bom dia! Gostaria de agendar uma consulta?</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 justify-end">
                    <div>
                      <p className="text-xs text-slate-500 text-right">Paciente</p>
                      <p className="text-sm font-medium text-slate-800">Sim, preciso de uma limpeza. Tem amanhã?</p>
                    </div>
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200">
                      <Users className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-blue-500">
                      <Mic className="h-3.5 w-3.5 text-white" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-display">AiCall.Odonto</p>
                      <p className="text-sm font-medium text-slate-800">Temos! 10h ou 14h30. Qual horário prefere?</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <motion.span
                      animate={{ opacity: [1, 0.3, 1] }}
                      transition={{ duration: 1.2, repeat: Infinity }}
                      className="text-[11px] text-emerald-600 font-semibold font-display"
                    >
                      Paciente falando...
                    </motion.span>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="mt-4 flex items-center justify-center gap-4">
                  <motion.button whileHover={{ scale: 1.08 }} className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50 border border-red-100 text-red-500 cursor-pointer focus-visible:ring-2 focus-visible:ring-red-500" aria-label="Desligar ligação">
                    <PhoneCall className="h-6 w-6 rotate-[135deg]" aria-hidden="true" />
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20 cursor-pointer focus-visible:ring-2 focus-visible:ring-cyan-500" aria-label="Iniciar chamada">
                    <PhoneCall className="h-7 w-7" aria-hidden="true" />
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.08 }} className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 cursor-pointer focus-visible:ring-2 focus-visible:ring-emerald-500" aria-label="Ativar microfone">
                    <Mic className="h-6 w-6" aria-hidden="true" />
                  </motion.button>
                </div>
              </motion.div>

              {/* Floating badges */}
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-2 -right-4 rounded-xl border border-cyan-100 bg-white px-4 py-3 shadow-lg"
              >
                <p className="text-xs font-semibold text-slate-400 font-display">Resposta em</p>
                <p className="text-lg font-bold text-cyan-600 font-display">3s</p>
              </motion.div>
              <motion.div
                animate={{ y: [0, 5, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -bottom-4 -left-6 rounded-xl border border-amber-100 bg-white px-4 py-3 shadow-lg"
              >
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 text-amber-400 fill-amber-400" aria-hidden="true" />
                  ))}
                </div>
                <p className="text-xs font-semibold text-slate-500 mt-1 font-display">4.9 de 200+ clínicas</p>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Stats bar */}
        <div className="relative border-t border-slate-100 bg-white/50 backdrop-blur">
          <div className="mx-auto grid max-w-7xl grid-cols-2 md:grid-cols-4">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex flex-col items-center py-8 px-4 border-r border-slate-100 last:border-r-0"
              >
                <p className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl tabular-nums font-display">
                  {s.value === 24 || s.value === 85 || s.value === 3 || s.value === 40 ? (
                    <AnimatedCounter from={0} to={s.value} suffix={s.suffix === 'h' || s.suffix === 's' ? s.suffix : s.suffix === '%' ? '%' : ''} />
                  ) : (
                    <>{s.value}{s.suffix}</>
                  )}
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-500 text-center font-display">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ CALL-ME / EXPERIMENTE ═══════════════════ */}
      <section id="experimente" className="py-20 sm:py-28 bg-gradient-to-br from-cyan-700 via-blue-700 to-cyan-800 text-white relative overflow-hidden" aria-labelledby="heading-experimente">
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
          className="absolute -top-20 -right-20 h-[400px] w-[400px] rounded-full bg-white/5 blur-3xl"
        />
        <motion.div
          animate={{ scale: [1.05, 1, 1.05] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
          className="absolute -bottom-20 -left-20 h-[300px] w-[300px] rounded-full bg-white/5 blur-3xl"
        />

        <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-white/10 backdrop-blur ring-1 ring-white/20 mb-8"
            >
              <PhoneCall className="h-10 w-10 text-white" aria-hidden="true" />
            </motion.div>
            <h2 id="heading-experimente" className="text-3xl font-extrabold tracking-tight sm:text-4xl font-display">
              Escute a IA em ação
            </h2>
            <p className="mt-4 text-lg text-cyan-100 max-w-2xl mx-auto font-serif">
              Coloque seu número e nossa IA vai te ligar em segundos. Experimente a qualidade da voz que atende seus pacientes e dispara ligações em lote para sua base de leads.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                alert('Em breve! Nossa IA vai ligar para você.')
              }}
              className="mt-10 mx-auto max-w-md"
            >
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <span className="sr-only">Número de Telefone</span>
                  <PhoneCall className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-cyan-300" aria-hidden="true" />
                  <input
                    type="tel"
                    name="phone"
                    aria-label="Número de telefone com DDD"
                    placeholder="(11) 99999-0000"
                    className="w-full h-14 rounded-xl bg-white/10 border-2 border-white/20 pl-11 pr-4 text-white placeholder:text-cyan-200/60 text-lg font-medium focus:outline-none focus:border-white/50 focus:bg-white/15 transition-all focus-visible:ring-2 focus-visible:ring-white"
                    required
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  className="inline-flex h-14 items-center justify-center gap-2 rounded-xl bg-white px-8 text-base font-bold text-cyan-800 shadow-xl shadow-black/20 hover:bg-cyan-50 transition-all shrink-0 focus-visible:ring-2 focus-visible:ring-white"
                >
                  Me ligue!
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </motion.button>
              </div>
              <p className="mt-4 flex items-center justify-center gap-2 text-sm text-cyan-200 font-display font-medium">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                Não enviaremos spam. Apenas uma ligação de demonstração.
              </p>
            </form>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-12 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2 text-sm text-cyan-100 backdrop-blur font-display font-medium">
              <Zap className="h-4 w-4 text-cyan-300" aria-hidden="true" />
              Após a ligação, crie sua conta e ative em 5 minutos
              <ArrowRight className="h-4 w-4 text-cyan-300" aria-hidden="true" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════ BENEFITS ═══════════════════ */}
      <section id="beneficios" className="py-20 sm:py-28 bg-slate-50 border-b border-slate-200" aria-labelledby="heading-beneficios">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl mx-auto text-center">
            <p id="heading-beneficios" className="text-sm font-bold uppercase tracking-widest text-cyan-700 font-display">Por que AiCall.Odonto</p>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl font-display">
              Mais pacientes atendidos e mais leads convertidos
            </h2>
            <p className="mt-4 text-lg text-slate-600 font-serif leading-relaxed">
              A IA atende quem liga e também liga para quem precisa ser lembrado. Sua clínica nunca mais perde uma oportunidade.
            </p>
          </Reveal>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08}>
                <motion.div
                  whileHover={{ y: -6, border: '1px solid #0891b2', boxShadow: '0 20px 40px -12px rgba(6, 182, 212, 0.12)' }}
                  className="rounded-2xl border border-slate-200 bg-white p-8 transition-all h-full shadow-sm"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-50 to-blue-50 text-cyan-600 ring-1 ring-cyan-100">
                    <b.icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-slate-900 font-display">{b.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500 font-sans">{b.description}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ FEATURES ═══════════════════ */}
      <section id="funcionalidades" className="py-20 sm:py-28 bg-white" aria-labelledby="heading-funcionalidades">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <p id="heading-funcionalidades" className="text-sm font-bold uppercase tracking-widest text-cyan-700 font-display">Funcionalidades</p>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl font-display">
              Tudo que sua clínica precisa em chamadas de voz
            </h2>
          </Reveal>

          <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08}>
                <motion.div whileHover={{ x: 6 }} className="flex gap-5">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20">
                    <f.icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 font-display">{f.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-500 font-sans">{f.text}</p>
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ HOW IT WORKS ═══════════════════ */}
      <section id="como-funciona" className="relative overflow-hidden bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900 text-white py-20 sm:py-28 border-y border-white/5" aria-labelledby="heading-como-funciona">
        <motion.div
          animate={{ scale: [1, 1.05, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-gradient-to-br from-cyan-500/10 to-blue-500/10 blur-3xl"
        />

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl mx-auto text-center">
            <p id="heading-como-funciona" className="text-sm font-bold uppercase tracking-widest text-cyan-400 font-display">Como funciona</p>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl font-display">
              Em 3 passos sua clínica começa a{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">atender e ligar</span>
            </h2>
          </Reveal>

          <div className="mt-16 grid gap-8 lg:grid-cols-3">
            {steps.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.15}>
                <div className="relative rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm p-8 h-full transition-colors hover:border-cyan-500/30">
                  <span className="text-5xl font-black text-cyan-500/20 font-display">{s.step}</span>
                  <h3 className="mt-4 text-xl font-bold font-display">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400 font-sans">{s.text}</p>
                  {i < steps.length - 1 && (
                    <div className="hidden lg:block absolute -right-5 top-1/2 -translate-y-1/2">
                      <ArrowRight className="h-6 w-6 text-cyan-500/40 animate-pulse" aria-hidden="true" />
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ TESTIMONIALS ═══════════════════ */}
      <section className="py-20 sm:py-28 bg-white" aria-labelledby="heading-depoimentos">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl mx-auto text-center">
            <p id="heading-depoimentos" className="text-sm font-bold uppercase tracking-widest text-cyan-700 font-display">Depoimentos</p>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl font-display">
              Quem já usa recomenda
            </h2>
          </Reveal>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {testimonials.map((t, i) => (
              <Reveal key={t.author} delay={i * 0.12}>
                <motion.div
                  whileHover={{ y: -6, border: '1px solid #0891b2', boxShadow: '0 20px 40px -12px rgba(6, 182, 212, 0.12)' }}
                  className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm h-full transition-all"
                >
                  <div className="flex gap-1" aria-label={`Avaliação de ${t.stars} estrelas`}>
                    {[...Array(t.stars)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" aria-hidden="true" />
                    ))}
                  </div>
                  <blockquote className="mt-4 text-base leading-relaxed text-slate-600 font-serif italic">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <div className="mt-6 border-t border-slate-100 pt-4">
                    <p className="text-sm font-bold text-slate-900 font-display">{t.author}</p>
                    <p className="text-xs text-slate-500 mt-0.5 font-sans">{t.role}</p>
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ PRICING ═══════════════════ */}
      <section id="planos" className="py-20 sm:py-28 bg-slate-50 border-t border-slate-200" aria-labelledby="heading-planos">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl mx-auto text-center">
            <p id="heading-planos" className="text-sm font-bold uppercase tracking-widest text-cyan-700 font-display">Planos</p>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl font-display">
              Simples e previsível. Sem surpresas.
            </h2>
            <p className="mt-4 text-lg text-slate-600 font-serif">
              Escolha seu plano e ative em 5 minutos. Cancele quando quiser.
            </p>
          </Reveal>

          <div className="mt-16 grid gap-8 lg:grid-cols-3">
            {plans.map((plan, i) => (
              <Reveal key={plan.name} delay={i * 0.1}>
                <motion.div
                  whileHover={plan.popular ? { scale: 1.02 } : { y: -6 }}
                  className={`relative rounded-2xl border bg-white p-8 h-full ${
                    plan.popular
                      ? 'border-cyan-500 ring-4 ring-cyan-500/10 shadow-xl shadow-cyan-100/50'
                      : 'border-slate-200'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 px-4 py-1 text-xs font-bold text-white shadow-lg shadow-cyan-500/30">
                        <Sparkles className="h-3 w-3" />
                        Mais popular
                      </span>
                    </div>
                  )}
                  <h3 className="text-lg font-bold text-slate-900">{plan.name}</h3>
                  <p className="mt-1 text-sm text-slate-500">{plan.desc}</p>
                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold text-slate-900">{plan.price}</span>
                    <span className="text-sm font-medium text-slate-500">/mês</span>
                  </div>
                  <ul className="mt-8 space-y-3">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-slate-600">
                        <CheckCircle2 className="h-5 w-5 shrink-0 text-cyan-500 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="mt-8">
                    <Link
                      to="/register"
                      className={`flex h-12 w-full items-center justify-center gap-2 rounded-xl text-sm font-bold transition-all ${
                        plan.popular
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/40'
                          : 'border-2 border-slate-200 text-slate-700 hover:border-cyan-300 hover:text-cyan-700 hover:bg-cyan-50'
                      }`}
                    >
                      Assinar agora
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </motion.div>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ FAQ ═══════════════════ */}
      <section id="faq" className="py-20 sm:py-28 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <p className="text-sm font-semibold uppercase tracking-widest text-cyan-600">Dúvidas</p>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Perguntas frequentes
            </h2>
          </Reveal>

          <dl className="mt-16 space-y-4">
            {faq.map((item, i) => (
              <Reveal key={item.q} delay={i * 0.06}>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-all hover:border-cyan-200 hover:bg-white">
                  <dt className="text-base font-bold text-slate-900">{item.q}</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-slate-500">{item.a}</dd>
                </div>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>

      {/* ═══════════════════ FINAL CTA ═══════════════════ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 py-20 sm:py-28 border-t border-white/5" aria-labelledby="heading-final-cta">
        <motion.div
          animate={{ scale: [1, 1.05, 1], rotate: [0, 45, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
          className="absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/20 blur-3xl"
        />
        <motion.div
          animate={{ scale: [1.05, 1, 1.05], rotate: [0, -30, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="absolute -bottom-20 -left-20 h-[400px] w-[400px] rounded-full bg-gradient-to-tr from-cyan-500/10 to-transparent blur-3xl"
        />

        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <Reveal>
            <h2 id="heading-final-cta" className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl font-display leading-tight">
              Escute a qualidade da nossa IA <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">agora</span>. Coloque seu número e receba uma ligação.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto font-serif leading-relaxed">
              Junte-se a mais de 200 clínicas que já automatizaram o atendimento telefônico e aumentaram o faturamento com IA de voz. Coloque seu número e receba uma ligação agora mesmo.
            </p>
          </Reveal>
          <Reveal delay={0.25} className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }}>
              <a
                href="#experimente"
                className="inline-flex h-14 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-10 text-base font-bold text-white shadow-xl shadow-cyan-500/25 font-display focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
              >
                Quero receber uma ligação
                <PhoneCall className="h-5 w-5" aria-hidden="true" />
              </a>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/register"
                className="inline-flex h-14 items-center justify-center gap-2 rounded-xl border-2 border-white/15 bg-white/5 px-10 text-base font-bold text-white backdrop-blur-sm hover:bg-white/10 hover:border-white/25 transition-all font-display focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
              >
                Criar conta e ativar
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </motion.div>
          </Reveal>
          <Reveal delay={0.35}>
            <p className="mt-8 text-sm text-slate-400 font-sans">
              Sem compromisso. Ativação em 5 minutos. Cancele quando quiser.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════ FOOTER ═══════════════════ */}
      <footer className="border-t border-slate-200 bg-white py-12" role="contentinfo">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 text-white">
                <PhoneCall className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="text-sm font-bold text-slate-900 font-display">
                AiCall<span className="text-cyan-600">.Odonto</span>
              </span>
            </div>
            <nav className="flex flex-wrap justify-center gap-6 text-sm font-semibold text-slate-500" aria-label="Navegação do rodapé">
              <a href="#beneficios" className="hover:text-cyan-600 transition-colors focus-visible:ring-2 focus-visible:ring-cyan-500 rounded px-1">Benefícios</a>
              <a href="#funcionalidades" className="hover:text-cyan-600 transition-colors focus-visible:ring-2 focus-visible:ring-cyan-500 rounded px-1">Funcionalidades</a>
              <a href="#planos" className="hover:text-cyan-600 transition-colors focus-visible:ring-2 focus-visible:ring-cyan-500 rounded px-1">Planos</a>
              <a href="#faq" className="hover:text-cyan-600 transition-colors focus-visible:ring-2 focus-visible:ring-cyan-500 rounded px-1">FAQ</a>
              <Link to="/login" className="hover:text-cyan-600 transition-colors focus-visible:ring-2 focus-visible:ring-cyan-500 rounded px-1">Entrar</Link>
            </nav>
            <p className="text-xs text-slate-400 font-sans">
              &copy; {new Date().getFullYear()} AiCall.Odonto. Todos os direitos reservados.
            </p>
          </div>
        </div>
        <div className="mt-8 border-t border-slate-100 pt-6 text-center">
          <a
            href="https://greenhub.buzz"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-emerald-600 transition-colors focus-visible:ring-2 focus-visible:ring-emerald-500 rounded px-1"
          >
            Feito com <Heart className="h-3.5 w-3.5 text-red-400" /> por <span className="font-semibold text-slate-500 hover:text-emerald-600">GreenHub.buzz</span>
          </a>
        </div>
      </footer>
    </main>
  )
}
